import React from "react";
import { Link } from "react-router-dom";

import Box from "veneer/Box";
import DataTable, { createColumnHelper, sorting } from "veneer/DataTable";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import { ENV } from "cortex/ui/src/constant/app";
import { useAppSelector } from "cortex/ui/src/hooks";
import {
  accountStatsStoresLoadedSelector,
  playerVersionStatsSelector,
} from "cortex/ui/src/redux-store/ui-state/account-stats/selectors";
import { TableDataSpinner } from "cortex/ui/src/ui/loader";
import { Panel } from "cortex/ui/src/view/layout";

declare function require(moduleName: string): unknown;

type SemverValid = (version: string | null | undefined) => string | null;
type SemverLt = (version: string, other: string) => boolean;

const semverValid = require("semver/functions/valid") as SemverValid;
const semverLt = require("semver/functions/lt") as SemverLt;

const { APP_NAME } = ENV;

function versionSort<T extends { version: string }>(
  a: { original: T },
  b: { original: T },
) {
  const verA = a.original.version;
  const verB = b.original.version;

  // Handle no_ver cases
  if (verA === "no_ver") return 1;
  if (verB === "no_ver") return -1;

  // Handle invalid versions (fall back to string comparison)
  const validA = semverValid(verA);
  const validB = semverValid(verB);

  if (!validA && !validB) return verA.localeCompare(verB);
  if (!validA) return 1;
  if (!validB) return -1;

  return semverLt(validA, validB) ? 1 : -1;
}

interface PlayerVersionTableRow {
  count: number;
  version: string;
}

const column = createColumnHelper<PlayerVersionTableRow>();

const columns = [
  column.accessor("version", {
    header: "Version",
    cell: (prop) => {
      const value = prop.getValue();
      return value === "no_ver" ? "No Version" : value;
    },
    sortingFn: versionSort,
  }),
  column.accessor("count", {
    header: "Player Count",
    cell: (prop) => {
      const row = prop.row.original;
      const value = prop.getValue();
      if (APP_NAME === "zeus") {
        return value;
      }
      const playerVersion = row.version || "no_ver";
      return (
        <Link
          title={`Devices with version ${playerVersion}`}
          to={{
            pathname: "/devices",
            state: { current_version: playerVersion },
          }}
        >
          {value}
        </Link>
      );
    },
    meta: { align: "right" },
    sortingFn: sorting.number((row) => row.count),
  }),
];

export default function PlayerVersionTable() {
  const stats = useAppSelector(playerVersionStatsSelector);
  const storesLoaded = useAppSelector(accountStatsStoresLoadedSelector);
  const noDataText = !storesLoaded ? (
    <TableDataSpinner title="Fetching data..." />
  ) : (
    "No data to display."
  );

  return (
    <Panel p={1} title="Player Versions">
      <Box mx={-1}>
        <DataTable
          columns={columns}
          emptyState={
            <Box p={2} textAlign="center">
              <Typography
                color={Colors.gray1000}
                component="span"
                variant="body2"
              >
                {noDataText}
              </Typography>
            </Box>
          }
          initialPageSize={10}
          initialSort={{ id: "version", desc: false }}
          paginationProps={{ showFirstButton: false, showLastButton: false }}
          rows={stats}
        />
      </Box>
    </Panel>
  );
}
